import { createApp } from "vue";
import App from "./App.vue";
import router from "./router";
import { store } from "./store";
import Notifications from "@kyvg/vue3-notification";
import i18n from "@/localization/i18n";
import { AuthActionTypes } from "@/store/modules/auth/action-types";
import VueSweetalert2 from "vue-sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import { Roles, Rules } from "@/helpers/enums/Roles";
import { GeneralActionTypes } from "@/store/modules/general/action-types";
import VueDiff from "vue-diff";
import "vue-diff/dist/index.css";
import filters from "./helpers/Filters";
import { JL } from "jsnlog";
import { createPinia } from 'pinia'
import { configure, defineRule } from 'vee-validate';
import PrimeVue from 'primevue/config';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import Button from "primevue/button";
import StyleClass from 'primevue/styleclass';
import Ripple from 'primevue/ripple';
import Dropdown from 'primevue/dropdown';
import Checkbox from 'primevue/checkbox';
import Tree from 'primevue/tree';
import InputText from 'primevue/inputtext';
import Tooltip from 'primevue/tooltip';
import MultiSelect from 'primevue/multiselect';
import Menu from 'primevue/menu';
import TabView from 'primevue/tabview'
import TabPanel from 'primevue/tabpanel'
import Textarea from 'primevue/textarea'
import InputSwitch from 'primevue/inputswitch';
import Accordion from 'primevue/accordion';
import AccordionTab from 'primevue/accordiontab';
import Dialog from 'primevue/dialog';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import VueTelInput from 'vue3-tel-input'
import 'vue3-tel-input/dist/vue3-tel-input.css'
import 'bootstrap/dist/css/bootstrap.min.css';
import "primeflex/primeflex.css";
import 'primevue/resources/themes/bootstrap4-light-blue/theme.css';
import 'primevue/resources/primevue.min.css';
import 'primeicons/primeicons.css';
import 'primevue/resources/themes/lara-light-blue/theme.css';
import '@vueform/slider/themes/default.css';


// Default values
configure({
  validateOnBlur: true, 
  validateOnChange: true, 
  validateOnInput: false, 
  validateOnModelUpdate: true,
});

JL.setOptions({
  defaultAjaxUrl: `${process.env.VUE_APP_AGSC_API}logging`,
});

const options = {
  confirmButtonColor: "#164969",
  cancelButtonColor: "white",
};

const pinia = createPinia()

const app = createApp(App)
  .use(i18n)
  .use(Notifications)
  .use(store)
  .use(router)
  .use(VueTelInput)
  .use(PrimeVue,{ripple: true})
  .use(VueSweetalert2, options)
  .use(VueDiff, {
    componentName: "VueDiff",
  })
  .use(pinia)
  .component('Column', Column)
  .component('Dropdown',Dropdown)
  .component('DataTable', DataTable)
  .component('Button', Button)
  .component("Checkbox",Checkbox)
  .component('Tree', Tree)
  .component('InputText', InputText)
  .component('MultiSelect',MultiSelect)
  .component('Menu',Menu)
  .component('TabView',TabView) 
  .component('TabPanel',TabPanel) 
  .component('Textarea',Textarea)
  .component('InputSwitch',InputSwitch)
  .component('Accordion',Accordion)
  .component('Dialog',Dialog)
  .component('AccordionTab',AccordionTab)
  .directive('Tooltip', Tooltip)
  .directive('styleclass', StyleClass)
  .directive('ripple', Ripple)
  .directive("role", {
    mounted(el: any, binding: any, vnode: any) {
      let role = (store.state.auth.user.find(f => f.type == "role") || {})
        .value;
      if (!process.env.NODE_ENV || process.env.NODE_ENV === "development") {
        role = Roles.AGMSAdmin;
        // role = Roles.PartnerAdmin;
        // role = Roles.CompanyAdmin;
        // role = Roles.SiteAudit;
        // role = Roles.GroupAdmin;
        // role = Roles.GroupAudit;
        // role = Roles.SiteAdmin;
      }

      if (binding.value && !binding.value.includes(role)) {
        vnode.el.parentElement.removeChild(vnode.el);
      }
    },
  })
  .directive("rule", {
    mounted(el: any, binding: any, vnode: any) {
      // if (binding.value && binding.value.includes(Rules.AGMSTemplateSelect)) {
      //   const agmsTemplate: any = store.state.auth.agmsTemplate;
      //   if (!agmsTemplate.mainid) {
      //     if (vnode.el.parentElement)
      //       vnode.el.parentElement.removeChild(vnode.el);
      //   }
      // }
      if (binding.value && binding.value.includes(Rules.AGMSGroupSelect)) {
        const agmsGroup: any = store.state.auth.agmsGroup;
        if (!agmsGroup.mainid) {
          if (vnode.el.parentElement)
            vnode.el.parentElement.removeChild(vnode.el);
        }
      }
    },
  });

app.config.globalProperties.$filters = filters;

if (!process.env.NODE_ENV || process.env.NODE_ENV !== "development") {
  app.config.errorHandler = function (err, vm, info) {
    console.log(err);
    console.log(vm);
    console.log(info);
    if(!err || err === 0) {
      return;
    }
    const error = {
      email: store.getters.loggedInUser ? store.getters.loggedInUser.email : "",
      exception: err,
      endpoint: window.location.pathname,
      browser: window.navigator.userAgent,
    };
    JL().error(error);
  };
}

app.mount("#app");

const loginUrl = "/bff/login";
const userUrl = "/bff/user";
const localApiUrl = "/local";
const remoteApiUrl = "/api";
let logoutUrl = "/bff/logout";

async function checkUser(): Promise<boolean> {
  store.dispatch(GeneralActionTypes.LOADER_STATUS, true);
  const req = new Request(userUrl, {
    headers: new Headers({
      "X-CSRF": "1",
    }),
  });
  try {
    const resp = await fetch(req);
    if (resp.ok) {
      const claims = await resp.json();
      
      if (localStorage.getItem("selectedRole")) {
        const role = localStorage.getItem("selectedRole");
        const roleIndex = claims.findIndex((i: any) => i.type === "role");
        if (roleIndex > -1) {
          claims[roleIndex].value = role;
        }
      }

      store.dispatch(AuthActionTypes.SET_USER, claims);
  
      setTimeout(() => {
        store.dispatch(GeneralActionTypes.LOADER_STATUS, false);
      }, 200);
      const logoutUrlClaim = claims.find(
        (claim: any) => claim.type === "bff:logout"
      );
      if (logoutUrlClaim) {
        logoutUrl = logoutUrlClaim.value;
      }
    } else if (resp.status === 401) {
      setTimeout(() => {
        store.dispatch(GeneralActionTypes.LOADER_STATUS, false);
      }, 200);
      localStorage.clear();
      sessionStorage.clear();
      const logoutUrl = `/bff/logout?sid=${
        store.getters.loggedInUser ? store.getters.loggedInUser.sid : ""
      }`;
      window.location.href = logoutUrl;
      return false;
    }
    return true;
  } catch (e) {
    setTimeout(() => {
      store.dispatch(GeneralActionTypes.LOADER_STATUS, false);
    }, 200);
    console.log("error checking user status");
    return false;
  }
}

if (!process.env.NODE_ENV || process.env.NODE_ENV !== "development") {
  router.beforeEach(async (to, from, next) => {
    const bg = document.querySelector(".modal-backdrop");
    if (bg) bg.remove();
    const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
    if (requiresAuth) {
      (await checkUser()) == true ? next() : (window.location.href = loginUrl);
    } else {
      next();
    }
  });
}
